import {
  APIError,
  fetcher,
  isAPIError,
  isRegionEntitlementDeniedError,
  useClientPortalAPI,
} from "@certn/api";
import { useAuth } from "@certn/auth";
import { useSWRConfig } from "swr";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { BootstrapLoadingOverlay } from "../components/BootstrapLoadingOverlay";
import { ConfigurationError } from "../components/ConfigurationError";
import { RegionEntitlementDeniedPage } from "../components/RegionEntitlementDeniedPage";
import { purgeProtectedSwrCache } from "../utils/protectedSwrCache";
import {
  CLIENT_PORTAL_ME_PATH,
  ClientPortalBootstrapContext,
  ACTIVATION_COALESCE_MS,
  isProtectedClientPortalApiRequest,
  type ClientPortalBootstrapStatus,
  type ClientPortalUserDetails,
  type RegionEntitlementDeniedBody,
} from "./clientPortalBootstrapContext";

type BootstrapEpochTaggedError = APIError & { bootstrapEpoch?: number };

function getBootstrapRequestEpoch(error: unknown): number | undefined {
  if (error && typeof error === "object" && "bootstrapEpoch" in error) {
    const epoch = (error as BootstrapEpochTaggedError).bootstrapEpoch;
    return typeof epoch === "number" ? epoch : undefined;
  }
  return undefined;
}

export function ClientPortalBootstrapProvider(props: PropsWithChildren) {
  const { children } = props;
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useAuth();

  const deniedRef = useRef(false);
  const activationEpochRef = useRef(0);
  const recoveryInProgressRef = useRef(false);
  const activationCoalesceTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [status, setStatus] = useState<ClientPortalBootstrapStatus>("pending");
  const statusRef = useRef<ClientPortalBootstrapStatus>(status);
  const [authorizedUserDetails, setAuthorizedUserDetails] = useState<
    ClientPortalUserDetails | undefined
  >(undefined);
  const [denialBody, setDenialBody] = useState<
    RegionEntitlementDeniedBody | undefined
  >(undefined);
  const [authorizationError, setAuthorizationError] = useState<
    unknown | undefined
  >(undefined);
  const initialAuthorizationStartedRef = useRef(false);

  const setBootstrapStatus = useCallback(
    (next: ClientPortalBootstrapStatus) => {
      statusRef.current = next;
      setStatus(next);
    },
    []
  );

  const { cache, mutate: boundMutate } = useSWRConfig();

  const purgeProtectedCacheWithoutRevalidation = useCallback(async () => {
    await purgeProtectedSwrCache(cache, boundMutate, [CLIENT_PORTAL_ME_PATH]);
  }, [boundMutate, cache]);

  const shouldIgnoreStaleBootstrapError = useCallback(
    (error: unknown, options?: { ignoreWhenAuthorized?: boolean }) => {
      const requestEpoch = getBootstrapRequestEpoch(error);

      if (
        requestEpoch !== undefined &&
        requestEpoch !== activationEpochRef.current
      ) {
        return true;
      }

      if (deniedRef.current && statusRef.current === "denied") {
        return true;
      }

      if (options?.ignoreWhenAuthorized && statusRef.current === "authorized") {
        return true;
      }

      return false;
    },
    []
  );

  const shouldIgnoreUntaggedAuthorizedAuthFailure = useCallback(
    (error: unknown) => {
      if (statusRef.current !== "authorized") {
        return false;
      }

      // Silent revalidation stamps bootstrapEpoch on /me/ failures. Untagged
      // auth failures while authorized are stale SWR callbacks from before
      // the current session epoch settled.
      return getBootstrapRequestEpoch(error) === undefined;
    },
    []
  );

  const meFetcher = useCallback((url: string) => fetcher.request(url), []);

  const handleRegionDenial = useCallback(
    async (error: APIError) => {
      if (!isRegionEntitlementDeniedError(error)) {
        return;
      }

      deniedRef.current = true;
      activationEpochRef.current += 1;

      setDenialBody(error.body as RegionEntitlementDeniedBody);
      setBootstrapStatus("denied");
      setAuthorizationError(error);
      setAuthorizedUserDetails(undefined);

      await purgeProtectedCacheWithoutRevalidation();
    },
    [purgeProtectedCacheWithoutRevalidation, setBootstrapStatus]
  );

  const shouldFetchMe =
    isAuthenticated && status !== "denied" && !deniedRef.current;

  const {
    error: meError,
    isLoading,
    isValidating,
    mutate: mutateUser,
  } = useClientPortalAPI(
    shouldFetchMe ? CLIENT_PORTAL_ME_PATH : null,
    undefined,
    {
      fetcher: meFetcher,
      revalidateOnMount: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (error) => {
        const applyError = () => {
          if (error?.status === 401) {
            if (
              shouldIgnoreStaleBootstrapError(error) ||
              shouldIgnoreUntaggedAuthorizedAuthFailure(error)
            ) {
              return;
            }

            logout();
            return;
          }

          if (isRegionEntitlementDeniedError(error)) {
            if (
              shouldIgnoreStaleBootstrapError(error) ||
              shouldIgnoreUntaggedAuthorizedAuthFailure(error)
            ) {
              return;
            }

            void handleRegionDenial(error);
            return;
          }

          // Generic /me/ failure delivered only to SWR onError (mutateUser
          // did not throw). Apply only for the current epoch and never
          // clobber an already-authorized or sticky-denied later epoch.
          if (
            shouldIgnoreStaleBootstrapError(error, {
              ignoreWhenAuthorized: true,
            })
          ) {
            return;
          }

          setAuthorizationError(error);
          setBootstrapStatus("error");
        };

        const requestEpoch = getBootstrapRequestEpoch(error);
        if (requestEpoch !== undefined) {
          applyError();
          return;
        }

        const errorEpoch = activationEpochRef.current;
        queueMicrotask(() => {
          if (errorEpoch !== activationEpochRef.current) {
            return;
          }

          applyError();
        });
      },
    }
  );

  const validateAuthorization = useCallback(async () => {
    if (!isAuthenticated || deniedRef.current) {
      return undefined;
    }

    const epoch = ++activationEpochRef.current;
    setBootstrapStatus("pending");
    setAuthorizationError(undefined);
    setAuthorizedUserDetails(undefined);

    await purgeProtectedSwrCache(cache, boundMutate);
    await mutateUser(undefined, { revalidate: false });

    try {
      const data = await mutateUser();
      if (deniedRef.current) {
        return undefined;
      }
      if (epoch !== activationEpochRef.current) {
        return undefined;
      }
      if (data) {
        setAuthorizedUserDetails(data);
        setBootstrapStatus("authorized");
        setDenialBody(undefined);
      } else {
        setAuthorizationError(
          new Error("Client Portal user details were not returned")
        );
        setBootstrapStatus("error");
      }
      return data;
    } catch (error) {
      if (deniedRef.current) {
        return undefined;
      }
      if (epoch !== activationEpochRef.current) {
        return undefined;
      }

      if (isAPIError(error) && error.status === 401) {
        logout();
        return undefined;
      }

      if (isRegionEntitlementDeniedError(error)) {
        await handleRegionDenial(error);
        return undefined;
      }

      setAuthorizationError(error);
      setBootstrapStatus("error");
      return undefined;
    }
  }, [
    boundMutate,
    cache,
    handleRegionDenial,
    isAuthenticated,
    logout,
    mutateUser,
    setBootstrapStatus,
  ]);

  const revalidateAuthorizationSilently = useCallback(async () => {
    if (
      !isAuthenticated ||
      deniedRef.current ||
      statusRef.current !== "authorized"
    ) {
      return undefined;
    }

    const epoch = ++activationEpochRef.current;

    try {
      const data = await mutateUser();
      if (deniedRef.current || epoch !== activationEpochRef.current) {
        return undefined;
      }
      if (data) {
        setAuthorizedUserDetails(data);
        setDenialBody(undefined);
      }
      return data;
    } catch (error) {
      if (deniedRef.current || epoch !== activationEpochRef.current) {
        return undefined;
      }

      if (isAPIError(error) && error.status === 401) {
        logout();
        return undefined;
      }

      if (isRegionEntitlementDeniedError(error)) {
        await handleRegionDenial(error);
        return undefined;
      }

      return undefined;
    }
  }, [handleRegionDenial, isAuthenticated, logout, mutateUser]);

  const scheduleSecurityRevalidation = useCallback(() => {
    if (!isAuthenticated || deniedRef.current) {
      return;
    }

    if (activationCoalesceTimeoutRef.current !== null) {
      clearTimeout(activationCoalesceTimeoutRef.current);
    }

    activationCoalesceTimeoutRef.current = setTimeout(() => {
      activationCoalesceTimeoutRef.current = null;
      if (statusRef.current === "authorized") {
        void revalidateAuthorizationSilently();
      } else {
        void validateAuthorization();
      }
    }, ACTIVATION_COALESCE_MS);
  }, [isAuthenticated, revalidateAuthorizationSilently, validateAuthorization]);

  const recoverFromDenialAfterSignup = useCallback(async () => {
    if (!denialBody?.can_create_client_account) {
      return;
    }

    deniedRef.current = false;
    activationEpochRef.current += 1;
    setDenialBody(undefined);
    setBootstrapStatus("pending");
    setAuthorizationError(undefined);
    setAuthorizedUserDetails(undefined);

    recoveryInProgressRef.current = true;
    try {
      await purgeProtectedCacheWithoutRevalidation();
      await validateAuthorization();
    } finally {
      recoveryInProgressRef.current = false;
    }
  }, [
    denialBody,
    purgeProtectedCacheWithoutRevalidation,
    setBootstrapStatus,
    validateAuthorization,
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      initialAuthorizationStartedRef.current = false;
      setBootstrapStatus("pending");
      return;
    }

    if (deniedRef.current || initialAuthorizationStartedRef.current) {
      return;
    }

    initialAuthorizationStartedRef.current = true;
    void validateAuthorization();
  }, [isAuthenticated, setBootstrapStatus, validateAuthorization]);

  useEffect(() => {
    if (!isAuthenticated || deniedRef.current) {
      return;
    }

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        scheduleSecurityRevalidation();
      }
    };
    const onOnline = () => scheduleSecurityRevalidation();

    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("online", onOnline);

    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("online", onOnline);
    };
  }, [isAuthenticated, scheduleSecurityRevalidation]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    fetcher.getBootstrapEpochForRequest = () => activationEpochRef.current;

    const removeListener = fetcher.addAPIErrorListener((error, context) => {
      if (!isProtectedClientPortalApiRequest(context.path, context.method)) {
        return;
      }

      if (!isRegionEntitlementDeniedError(error)) {
        return;
      }

      const applyDenial = () => {
        if (recoveryInProgressRef.current) {
          return;
        }

        if (shouldIgnoreStaleBootstrapError(error)) {
          return;
        }

        void handleRegionDenial(error);
      };

      const requestEpoch = getBootstrapRequestEpoch(error);
      if (requestEpoch !== undefined) {
        applyDenial();
        return;
      }

      const errorEpoch = activationEpochRef.current;
      queueMicrotask(() => {
        if (errorEpoch !== activationEpochRef.current) {
          return;
        }

        applyDenial();
      });
    });

    return () => {
      fetcher.getBootstrapEpochForRequest = null;
      removeListener();
    };
  }, [handleRegionDenial, isAuthenticated, shouldIgnoreStaleBootstrapError]);

  useEffect(() => {
    return () => {
      if (activationCoalesceTimeoutRef.current !== null) {
        clearTimeout(activationCoalesceTimeoutRef.current);
      }
    };
  }, []);

  const isAuthorizationPending =
    status === "pending" ||
    (status !== "authorized" && (isLoading || isValidating));

  const contextValue = useMemo(
    () => ({
      status,
      userDetails: authorizedUserDetails,
      denialBody,
      isAuthorizationPending,
      authorizationError: authorizationError ?? meError,
      mutateUser: validateAuthorization,
      purgeProtectedCache: purgeProtectedCacheWithoutRevalidation,
      recoverFromDenialAfterSignup,
    }),
    [
      authorizationError,
      authorizedUserDetails,
      denialBody,
      isAuthorizationPending,
      meError,
      purgeProtectedCacheWithoutRevalidation,
      recoverFromDenialAfterSignup,
      status,
      validateAuthorization,
    ]
  );

  if (status === "denied" && denialBody) {
    return (
      <ClientPortalBootstrapContext.Provider value={contextValue}>
        <RegionEntitlementDeniedPage
          denialBody={denialBody}
          onSignupSuccess={recoverFromDenialAfterSignup}
          purgeProtectedCache={purgeProtectedCacheWithoutRevalidation}
        />
      </ClientPortalBootstrapContext.Provider>
    );
  }

  if (status === "error") {
    return (
      <ClientPortalBootstrapContext.Provider value={contextValue}>
        <ConfigurationError
          message={t(
            "It looks like something has gone wrong on our side, please refresh or try again later."
          )}
        />
      </ClientPortalBootstrapContext.Provider>
    );
  }

  if (isAuthorizationPending) {
    return (
      <ClientPortalBootstrapContext.Provider value={contextValue}>
        <BootstrapLoadingOverlay />
      </ClientPortalBootstrapContext.Provider>
    );
  }

  if (status !== "authorized") {
    return (
      <ClientPortalBootstrapContext.Provider value={contextValue}>
        <BootstrapLoadingOverlay />
      </ClientPortalBootstrapContext.Provider>
    );
  }

  return (
    <ClientPortalBootstrapContext.Provider value={contextValue}>
      {children}
    </ClientPortalBootstrapContext.Provider>
  );
}

export {
  ACTIVATION_COALESCE_MS,
  CLIENT_PORTAL_ME_PATH,
  ClientPortalBootstrapContext,
  isProtectedClientPortalCacheKey,
  useClientPortalBootstrap,
  useOptionalClientPortalBootstrap,
} from "./clientPortalBootstrapContext";
